import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Badge,
  Collapse,
  Box,
} from "@mui/material";
import {
  MoreVert,
  Visibility,
  Edit,
  Delete,
  CheckCircleOutline,
  CancelOutlined,
  SwapHoriz,
  Message,
  Input,
  Download,
  SyncOutlined,
  AttachFile,
  NoteAddOutlined,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BsFiletypeJson } from "react-icons/bs";
import { FiCornerUpRight } from "react-icons/fi";
import { BiError } from "react-icons/bi";
import TableLoader from "../common/TableLoader";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const AppTable = ({
  columns,
  data,
  loading,
  onView,
  onEdit,
  canEditRow,
  onDelete,
  onJsonView,
  onPreAuth,
  onCheck,
  onCancel,
  onConvert,
  onFollowUp,
  onCommunication,
  onErrorLogs,
  onProcess,
  onAttachment,
  onGenerate,
  selectable = false,
  selectedRows = [],
  onSelectAll,
  onSelectRow,
  actions = true,
  LoaderComponent,
  collapsible = false,
  renderExpansion,
  onDownload,
  bodyRowPadding = "4px",
  containerClassName = "custom-table-scrollbar",
  cellClassName = "",
  actioncellClassName = "",
  getRowSx,
  customActions = [],
  renderRowBelow,
}) => {
  const { t, i18n } = useTranslation();
  const alignStart = i18n.dir && i18n.dir() === "rtl" ? "right" : "left";
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openRow, setOpenRow] = useState(null);

  const handleOpenMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const hasErrorLogs = (row) =>
    (Array.isArray(row?.validation_errors) &&
      row.validation_errors.length > 0) ||
    row?.has_validation_error === true ||
    (Array.isArray(row?.error_logs) && row.error_logs.length > 0);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className={`border !rounded-lg overflow-x-auto overflow-y-hidden ${containerClassName}`}
      sx={{ border: "1px solid #e2e8f0" }}
    >
      {loading ? (
        <TableLoader columns={columns.length} rows={5} />
      ) : (
        <Table
          size="small"
          stickyHeader
          sx={{
            // minWidth: columns.length * 150,
            tableLayout: "auto",
          }}
        >
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    backgroundColor: "#d1d5db",
                    width: "30px",
                    borderBottom: "2px solid #d1d5db !important",
                  }}
                >
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded border-gray-300 text-[#0070C2] cursor-pointer focus:ring-0 focus:ring-offset-0 transition-all accent-[#0070C2]"
                    onChange={(e) => onSelectAll(e.target.checked)}
                    checked={
                      data?.length > 0 && selectedRows?.length === data?.length
                    }
                  />
                </TableCell>
              )}
              {actions && (
                <TableCell
                  align={alignStart}
                  sx={{
                    backgroundColor: "#d1d5db",
                    fontWeight: 800,
                    py: "2px",
                    px: 1.5,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    width: "1%",
                    color: "#1e293b",
                    borderBottom: "2px solid #d1d5db !important",
                    borderRight: "none !important",
                  }}
                >
                  {t("Actions")}
                </TableCell>
              )}
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  align={col.align || alignStart}
                  sx={{
                    backgroundColor: "#d1d5db",
                    fontWeight: 800,
                    py: "2px",
                    px: 1.5,
                    color: "#1e293b",
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    letterSpacing: "0.025em",
                    textTransform: "uppercase",
                    borderBottom: "2px solid #d1d5db !important",
                    borderRight: "none !important",
                    borderLeft: "none !important",
                    ...(col.width && {
                      width: col.width,
                      minWidth: col.width,
                    }),
                    ...(col.headerSx || col.sx || {}),
                  }}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              LoaderComponent
            ) : data && data.length > 0 ? (
              data.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    //key={rowIndex}
                    onClick={() =>
                      collapsible &&
                      setOpenRow(openRow === rowIndex ? null : rowIndex)
                    }
                    hover
                    sx={getRowSx ? getRowSx(row) : {}}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 rounded border-gray-300 text-[#0070C2] cursor-pointer focus:ring-0 focus:ring-offset-0 transition-all accent-[#0070C2]"
                          checked={selectedRows.includes(
                            row.id || row.transaction_id,
                          )}
                          onChange={(e) => {
                            e.stopPropagation(); // Yeh line zaroori hai
                            onSelectRow(row.id || row.transaction_id);
                          }}
                        />
                      </TableCell>
                    )}
                    {actions && (
                      <TableCell
                        className={actioncellClassName}
                        align={alignStart}
                        sx={{ py: bodyRowPadding, px: 1.5, width: "1%" }}
                      >
                        <div
                          className="grid gap-x-2 gap-y-1 w-max"
                          style={{
                            gridTemplateColumns: "repeat(4, max-content)",
                          }}
                        >
                          {onProcess && (
                            <div
                              title={t("Process")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onProcess(row);
                              }}
                            >
                              <SyncOutlined sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onAttachment && (
                            <div
                              title={t("Attachment")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAttachment(row);
                              }}
                            >
                              <AttachFile
                                sx={{
                                  fontSize: 19,
                                  transform: "rotate(90deg)",
                                }}
                              />
                            </div>
                          )}
                          {onCommunication && (
                            <div
                              title={t("Communications")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCommunication(row);
                              }}
                            >
                              <Badge
                                color="secondary"
                                badgeContent={row?.communication_count || 0}
                                showZero
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right",
                                }}
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: 8,
                                    height: 12,
                                    minWidth: 12,
                                    bottom: 4,
                                    right: 4,
                                  },
                                }}
                              >
                                <Message sx={{ fontSize: 19 }} />
                              </Badge>
                            </div>
                          )}
                          {onConvert && (
                            <div
                              title={t("Convert To Claim")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onConvert(row);
                              }}
                            >
                              <SwapHoriz sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onView && (
                            <div
                              title={t("View")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onView(row);
                              }}
                            >
                              <Visibility sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onCheck && (
                            <div
                              title={t("Check Status")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCheck(row);
                              }}
                            >
                              <CheckCircleOutline sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onCancel && (
                            <div
                              title={t("Cancel")}
                              className="cursor-pointer flex items-center justify-center text-red-500 hover:text-red-700 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCancel(row);
                              }}
                            >
                              <CancelOutlined sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onEdit && (!canEditRow || canEditRow(row)) && (
                            <div
                              title={t("Edit")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(row);
                              }}
                            >
                              <Edit sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onFollowUp && (
                            <div
                              title={t("Follow Up")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onFollowUp(row);
                              }}
                            >
                              <Input sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onJsonView && (
                            <div
                              title={t("JSON View")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onJsonView(row);
                              }}
                            >
                              <BsFiletypeJson className="text-[1rem]" />
                            </div>
                          )}
                          {onDownload && (
                            <div
                              title={t("Download")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownload(row);
                              }}
                            >
                              <Download sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onGenerate && (
                            <div
                              title={t("Generate")}
                              className="cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onGenerate(row);
                              }}
                            >
                              <NoteAddOutlined sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {customActions
                            .filter((action) =>
                              action.shouldRender ? action.shouldRender(row) : true
                            )
                            .map((action, actionIndex) => (
                              <div
                                key={actionIndex}
                                title={t(action.title)}
                                className={
                                  action.className ||
                                  "cursor-pointer flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick(row);
                                }}
                              >
                                {action.icon}
                              </div>
                            ))}
                          {onDelete && (
                            <div
                              title={t("Delete")}
                              className="cursor-pointer flex items-center justify-center text-red-500 hover:text-red-700 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(row);
                              }}
                            >
                              <Delete sx={{ fontSize: 19 }} />
                            </div>
                          )}
                          {onErrorLogs && hasErrorLogs(row) && (
                            <div
                              title={t("Error Logs")}
                              className="cursor-pointer flex items-center justify-center text-red-500 hover:text-red-700 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                onErrorLogs(row);
                              }}
                            >
                               <ErrorOutlineIcon
                                  style={{
                                    color: "#EAB308",
                                    cursor: "pointer",
                                    fontSize: "20px",
                                  }}
                                  // onClick={() => handleShowValidationErrors(item.validation_errors)}
                                  titleAccess={t("View Validation Errors")}
                                />
                            </div>
                          )}
                        </div>
                      </TableCell>
                    )}
                    {(() => {
                      let skipCount = 0;
                      return columns.map((col, colIdx) => {
                        if (skipCount > 0) {
                          skipCount--;
                          return null;
                        }
                        const rendered = col.render
                          ? col.render(row, rowIndex)
                          : row[col.field];
                        const isColSpanObj =
                          rendered &&
                          typeof rendered === "object" &&
                          !React.isValidElement(rendered) &&
                          rendered.colSpan;
                        const cellColSpan = isColSpanObj
                          ? rendered.colSpan
                          : 1;
                        const cellContent = isColSpanObj
                          ? rendered.content
                          : rendered;
                        if (cellColSpan > 1) {
                          skipCount = cellColSpan - 1;
                        }
                        return (
                          <TableCell
                            key={colIdx}
                            align={col.align || alignStart}
                            className={cellClassName}
                            colSpan={cellColSpan}
                            sx={{
                              py: bodyRowPadding,
                              px: 1.5,
                              fontSize: "0.80rem",
                              color: "#334155",
                              whiteSpace: "nowrap",
                              ...(col.width && {
                                width: col.width,
                                minWidth: col.width,
                              }),
                              ...(col.cellSx || col.sx || {}),
                            }}
                          >
                            {cellContent}
                          </TableCell>
                        );
                      });
                    })()}
                  </TableRow>
                  {renderRowBelow && renderRowBelow(row, rowIndex) && (
                    <TableRow>
                      <TableCell
                        colSpan={
                          columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)
                        }
                        sx={{ py: 0, px: 1.5, borderBottom: "none" }}
                      >
                        {renderRowBelow(row, rowIndex)}
                      </TableCell>
                    </TableRow>
                  )}
                  {collapsible && (
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={columns.length + 1}
                      >
                        <Collapse
                          in={openRow === rowIndex}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 1 }}>
                            {renderExpansion ? renderExpansion(row) : null}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  align="center"
                  sx={{ py: 1 }}
                >
                  {t("No Data Found")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};
export default AppTable;
